import React, { useState, useMemo } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function PumpTab({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onExport,
  pumpStats = {},
  pumpRecords = [],
  onDownloadPump,
}) {
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // Dynamically compute monthly running hours from real records
  const monthlyData = useMemo(() => {
    const monthMap = {};
    pumpRecords.forEach((r) => {
      let mKey = 'Current';
      if (r.rawDate) {
        mKey = moment(r.rawDate).format('MMM YYYY');
      } else if (r.date) {
        const parsed = moment(r.date, ['Do MMM, YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY']);
        if (parsed.isValid()) {
          mKey = parsed.format('MMM YYYY');
        }
      }
      const dayHours =
        r.decimalHours !== undefined
          ? Number(r.decimalHours)
          : ((parseFloat(r.a) || 0) + (parseFloat(r.b) || 0) + (parseFloat(r.c) || 0));
      monthMap[mKey] = (monthMap[mKey] || 0) + dayHours;
    });

    const labels = Object.keys(monthMap).length > 0 ? Object.keys(monthMap) : ['Current Range'];
    const data =
      Object.keys(monthMap).length > 0
        ? Object.values(monthMap).map((v) => Number(v.toFixed(2)))
        : [parseFloat(pumpStats.totalHours) || 0];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: '#1072bb',
          borderRadius: 6,
          barThickness: labels.length <= 2 ? 32 : 20,
        },
      ],
    };
  }, [pumpRecords, pumpStats.totalHours]);

  const formatHoursToHuman = (decimalHours) => {
    if (isNaN(decimalHours) || decimalHours === null || decimalHours === undefined) return '0 hrs 0 mins';
    const totalMinutes = Math.round(Number(decimalHours) * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs} hrs ${mins} mins`;
  };

  const monthlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0b2e45',
        callbacks: {
          label: (c) => ` ${formatHoursToHuman(c.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  // Dynamically compute shift-wise running hours from real records
  const shiftDonutData = useMemo(() => {
    let a = 0,
      b = 0,
      c = 0;
    pumpRecords.forEach((r) => {
      a += r.rawA !== undefined ? r.rawA : (parseFloat(r.a) || 0);
      b += r.rawB !== undefined ? r.rawB : (parseFloat(r.b) || 0);
      c += r.rawC !== undefined ? r.rawC : (parseFloat(r.c) || 0);
    });
    return {
      labels: ['Shift A (12 AM–8 AM)', 'Shift B (8 AM–4 PM)', 'Shift C (4 PM–12 AM)'],
      datasets: [
        {
          data: [Number(a.toFixed(2)), Number(b.toFixed(2)), Number(c.toFixed(2))],
          backgroundColor: ['#ef6b6f', '#1072bb', '#2ec98a'],
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  }, [pumpRecords]);

  const shiftDonutOptions = {
    cutout: '70%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#5b7482', boxWidth: 10, font: { size: 11 }, padding: 12 },
      },
      tooltip: {
        backgroundColor: '#0b2e45',
        callbacks: {
          label: (c) => ` ${c.label}: ${formatHoursToHuman(c.raw)}`,
        },
      },
    },
  };

  // Dynamically compute daily running trend line from all real records in range
  const trendData = useMemo(() => {
    const sorted = [...pumpRecords].reverse();
    const labels = sorted.map((r) => r.date);
    const data = sorted.map((r) => {
      if (r.decimalHours !== undefined) {
        return Number(r.decimalHours);
      }
      return Number(((parseFloat(r.a) || 0) + (parseFloat(r.b) || 0) + (parseFloat(r.c) || 0)).toFixed(2));
    });

    return {
      labels: labels.length > 0 ? labels : ['Today'],
      datasets: [
        {
          data: data.length > 0 ? data : [0],
          borderColor: '#1072bb',
          backgroundColor: 'rgba(16,114,187,.12)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: '#1072bb',
          borderWidth: 2,
        },
      ],
    };
  }, [pumpRecords]);

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0b2e45',
        callbacks: {
          label: (c) => ` ${formatHoursToHuman(c.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  const totalPages = Math.max(1, Math.ceil(pumpRecords.length / pageSize));
  const displayed = pumpRecords.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="pane active">
      <div className="pane-tools">
        <h2>
          Pump performance <span className="sub" style={{ fontWeight: 600, color: 'var(--muted)' }}>· motor analytics</span>
        </h2>
        <div className="range-ctl">
          <span className="rlab">From</span>
          <input
            type="date"
            className="rng-from"
            value={fromDate}
            onChange={(e) => onFromDateChange(e.target.value)}
          />
          <span className="rlab">To</span>
          <input
            type="date"
            className="rng-to"
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
          />
          <button className="btn xls" onClick={onExport}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            Excel
          </button>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="stat-strip" style={{ marginBottom: '16px' }}>
        <div className="stat" style={{ '--tint': 'rgba(16,114,187,.08)' }}>
          <div className="s-ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
          </div>
          <div className="s-lab">Total pump hours</div>
          <div className="s-val">
            <span>{pumpStats.totalHours || '0 hrs 0 mins'}</span>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(14,164,114,.1)' }}>
          <div className="s-ic" style={{ background: 'var(--ok)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15" />
            </svg>
          </div>
          <div className="s-lab">Operational days</div>
          <div className="s-val">
            <span>{pumpStats.cycles || '0 days'}</span>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(231,154,18,.12)' }}>
          <div className="s-ic" style={{ background: 'var(--warn)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20M2 12h20" />
            </svg>
          </div>
          <div className="s-lab">Yesterday running</div>
          <div className="s-val">
            <span>{pumpStats.yesterdayRunning || '0 hrs 0 mins'}</span>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(58,168,224,.14)' }}>
          <div className="s-ic" style={{ background: 'var(--surf)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="M7 14l3-3 3 3 4-6" />
            </svg>
          </div>
          <div className="s-lab">This month running</div>
          <div className="s-val">
            <span>{pumpStats.monthRunning || '0 hrs 0 mins'}</span>
          </div>
        </div>
      </div>

      {/* Row of 3 charts */}
      <div className="row three">
        <div className="client-card">
          <div className="card-h">
            <h3>Monthly running hours</h3>
          </div>
          <div className="pad">
            <div className="chart-box chart-sm">
              <Bar data={monthlyData} options={monthlyOptions} />
            </div>
          </div>
        </div>

        <div className="client-card">
          <div className="card-h">
            <h3>Shift-wise running</h3>
          </div>
          <div className="pad">
            <div className="donut-box">
              <Doughnut data={shiftDonutData} options={shiftDonutOptions} />
              <div className="donut-center has-legend pump-donut-center">
                <div className="donut-val">{pumpStats.totalHours || '0 hrs 0 mins'}</div>
                <small>total</small>
              </div>
            </div>
          </div>
        </div>

        <div className="client-card">
          <div className="card-h">
            <h3>Daily running trend</h3>
          </div>
          <div className="pad">
            <div className="chart-box chart-sm">
              <Line data={trendData} options={trendOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Pump running records table */}
      <div className="client-card" style={{ marginTop: '16px' }}>
        <div className="card-h">
          <h3>Pump running-hours records</h3>
          <div className="btnrow">
            <button className="icon-dl xls" title="Download Excel" onClick={() => onDownloadPump('xlsx')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M9.5 13l5 5M14.5 13l-5 5" />
              </svg>
            </button>
            <button className="icon-dl pdf" title="Download PDF" onClick={() => onDownloadPump('pdf')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M9 14h6M9 17.5h4" />
              </svg>
            </button>
          </div>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Pump hours (cum.)</th>
                <th>Daily running</th>
                <th title="Shift A: 12:00 AM to 08:00 AM">Shift A (12 AM–8 AM)</th>
                <th title="Shift B: 08:00 AM to 04:00 PM">Shift B (8 AM–4 PM)</th>
                <th title="Shift C: 04:00 PM to 12:00 AM">Shift C (4 PM–12 AM)</th>
                <th>Cycles</th>
                <th>Avg Flow (m³/hr)</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length > 0 ? (
                displayed.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td>{r.cum}</td>
                    <td>{r.daily}</td>
                    <td>{r.a}</td>
                    <td>{r.b}</td>
                    <td>{r.c}</td>
                    <td>{r.cyc}</td>
                    <td>{r.avgFlowrate || '0.00'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', color: '#8aa1ad', padding: '24px' }}>
                    No pump running records found for selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pumpRecords.length > 0 && (
          <div className="tbl-foot">
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, pumpRecords.length)} of{' '}
              {pumpRecords.length}
            </span>
            <div className="pager">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ‹
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
